/**
 * Type guard for checking if a value is a valid enum value.
 */
export const isValidValue = <T extends Record<string, string>>(
  value: string | null | undefined,
  obj: T
): value is T[keyof T] => {
  return Object.values(obj).includes(value as T[keyof T])
}

/**
 * Wrapper for Object.values() with type assertion.
 * This is safe for string enums but would become unsafe if extended to permit numeric enums
 *
 * Note: this will allow you to pass non-enum objects because typescript does not have a way
 * to enforce that an object is an enum
 */
export const values = <T extends Record<string, string>>(obj: T): T[keyof T][] => {
  return Object.values(obj) as T[keyof T][]
}

/**
 * Wrapper for Object.keys() with type assertion.
 * This is safe for string enums but would become unsafe if extended to permit numeric enums
 *
 * Note: this will allow you to pass non-enum objects because typescript does not have a way
 * to enforce that an object is an enum
 */
export const keys = <T extends Record<string, string>>(obj: T): (keyof T)[] => {
  return Object.keys(obj) as (keyof T)[]
}

/**
 * Reverses the keys and values of a string enum-like object.
 *
 * Note: this will allow you to pass non-enum objects because typescript does not have a way
 * to enforce that an object is an enum. Ensure that the values are unique strings.
 * If there are duplicate values, the last key will overwrite previous ones.
 */
export const reverse = <T extends Record<string, string>>(obj: T): Record<T[keyof T], keyof T> => {
  return Object.fromEntries(Object.entries(obj).map(([key, value]) => [value, key])) as Record<
    T[keyof T],
    keyof T
  >
}

/**
 * Extends a string enum-like object with additional key-value pairs.
 */
export const extend = <T extends Record<string, string>, U extends Record<string, string>>(
  base: T,
  extension: U
): Readonly<T & U> => {
  return { ...base, ...extension }
}

/**
 * Omits specified keys from a string enum-like object.
 */
export const omit = <T extends Record<string, string>, K extends keyof T>(
  obj: T,
  keysToOmit: K[]
): Readonly<Omit<T, K>> => {
  const result = { ...obj }
  for (const key of keysToOmit) {
    delete result[key]
  }
  return result
}
